/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import {
  FORM_FIELDS_NAMESPACE,
  formFieldsTranslations,
} from '@opengeoweb/form-fields';
import {
  safelyInitI18nForTestsAndStories,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import swTranslations from '../../../locales/spaceweather.json';

export const SPACE_WEATHER_NAMESPACE = 'spaceweather';

export const initSpaceweatherI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, SPACE_WEATHER_NAMESPACE, [
    { ns: SPACE_WEATHER_NAMESPACE, translations: swTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: FORM_FIELDS_NAMESPACE, translations: formFieldsTranslations },
  ]);
};

const ns = [SPACE_WEATHER_NAMESPACE, SHARED_NAMESPACE, FORM_FIELDS_NAMESPACE];

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns, ...params });

export const useSpaceweatherTranslation = (): UseTranslationResponse<
  typeof SPACE_WEATHER_NAMESPACE,
  typeof i18n
> => useTranslation(ns);

export { i18n };
